import { sanitize } from '@ben/common/dom';
import RiQuestionLine from '@ben/icons/question-line';
import { Checkbox as AriaCheckbox } from 'ariakit/checkbox';
import type {
  ChangeEvent,
  ForwardedRef,
  MouseEvent,
  ReactNode,
  JSX,
} from 'react';
import { forwardRef, memo, useCallback, useId } from 'react';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { css } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import { TypographyVariant, theme } from '../../utils/theme';
import InputFeedback from '../input-feedback';
import RIcon from '../remix-icon/remix-icon';
import Tooltip from '../tooltip';
import Typography from '../typography/typography';

export interface CheckboxProps {
  id?: string;
  label?: ReactNode;
  showLabel?: boolean;
  defaultChecked?: boolean;
  value: boolean;
  typographyVariant?: TypographyVariant;
  onChange: (value: boolean) => void;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  size?: string;
  hasError?: boolean;
  className?: string;
  indeterminate?: boolean;
  errorMessage?: string;
  warningMessage?: string;
  successMessage?: string;
  hintText?: string | ReactNode;
}

export const Checkbox = forwardRef(
  (
    {
      id,
      label = '',
      showLabel = true,
      value,
      typographyVariant = TypographyVariant.bodyLg1,
      onChange,
      defaultChecked,
      disabled = false,
      hasError = false,
      indeterminate = false,
      className,
      errorMessage,
      warningMessage,
      successMessage,
      hintText,
      ...rest
    }: CheckboxProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const defaultId = useId();
    const checkboxId = id ?? defaultId;

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.checked);
      },
      [onChange]
    );

    const isLabelString = typeof label === 'string';
    const sanitizedLabel = isLabelString ? sanitize(label) : '';

    return (
      <div>
        <Label htmlFor={checkboxId} showLabel={showLabel} className={className}>
          <StyledCheckbox
            ref={ref}
            id={checkboxId}
            defaultChecked={defaultChecked}
            checked={value}
            onChange={handleChange}
            disabled={disabled}
            $hasError={hasError}
            $isCheckedOrIndeterminate={value || indeterminate}
            {...rest}
            aria-label={label}
            data-testid={
              isLabelString
                ? `${label.replaceAll(' ', '_')}-checkbox`
                : undefined
            }
            data-indeterminate={indeterminate}
          />
          {showLabel && (
            <HintWrapper>
              {isLabelString ? (
                <Typography
                  variant={typographyVariant}
                  color={theme.palette.neutral[disabled ? 70 : 100]}
                  dangerouslySetInnerHTML={{ __html: sanitizedLabel }}
                />
              ) : (
                label
              )}
              {hintText && (
                <Tooltip
                  tooltipText={hintText}
                  id={checkboxId}
                  tooltipAnchorStyle={{
                    height: '10px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <RIcon
                    icon={RiQuestionLine}
                    size="sm"
                    color={theme.palette.neutral[80]}
                  />
                </Tooltip>
              )}
            </HintWrapper>
          )}
        </Label>
        <StyledInputFeedback
          errorMessage={errorMessage}
          warningMessage={warningMessage}
          successMessage={successMessage}
        />
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default memo(Checkbox);

const StyledCheckbox: StyledComponent<
  ({
    label,
    size,
    $hasError,
    $isCheckedOrIndeterminate,
    ...rest
  }: CheckboxProps & {
    $hasError: boolean;
    $isCheckedOrIndeterminate: boolean;
  }) => JSX.Element,
  DefaultTheme,
  Record<string, unknown>
> = styled(AriaCheckbox)`
  ${({
    size,
    $hasError,
    $isCheckedOrIndeterminate,
  }: {
    size?: string;
    $hasError?: boolean;
    $isCheckedOrIndeterminate: boolean;
  }) => css`
    width: ${size ?? '20px'};
    height: ${size ?? '20px'};
    min-width: ${size ?? '20px'};
    min-height: ${size ?? '20px'};
    -moz-appearance: none;
    -webkit-appearance: none;
    -o-appearance: none;
    background: ${({ theme }) => theme.palette.neutral['00']};
    border: 1px solid
      ${({ theme }) =>
        $hasError ? theme.palette.error.main : theme.palette.neutral[80]};
    border-radius: 3px;
    transition: color 0.2s ease-in-out;
    cursor: pointer;

    &:hover:not(:disabled):not(:checked):not([data-indeterminate='true']) {
      background: ${({ theme }) => theme.palette.neutral[50]};
    }

    &:focus-visible {
      outline: ${({ theme }) => theme.palette.focus} solid 2px;
      outline-offset: 1px !important;
    }

    &:checked {
      background: ${({ theme }) => theme.palette.primary.main};
      background-image: url('data:image/svg+xml;utf8,<svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 3.5L4 6.5L9.5 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    ');
      background-repeat: no-repeat;
      background-position: center;
      border: none;
    }

    &[data-indeterminate='true'] {
      background: ${({ theme }) => theme.palette.primary.main};
      background-image: url('data:image/svg+xml;utf8,<svg width="10" height="2" viewBox="0 0 14 2" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 0C0.447716 0 0 0.447715 0 1V1C0 1.55228 0.447715 2 1 2H13C13.5523 2 14 1.55228 14 1V1C14 0.447715 13.5523 0 13 0H1Z" fill="white"/>
    </svg>
    ');
      background-repeat: no-repeat;
      background-position: center;
      border: none;
    }

    &:disabled {
      cursor: not-allowed;
      pointer-events: all !important;
      background: ${({ theme }) =>
        $isCheckedOrIndeterminate
          ? theme.palette.primary
          : theme.palette.neutral[40]};
      border: 1px solid
        ${({ theme }) =>
          $isCheckedOrIndeterminate
            ? theme.palette.neutral[80]
            : theme.palette.neutral[60]};
      opacity: ${$isCheckedOrIndeterminate ? 0.4 : 1};
    }
  `}
`;

const Label: StyledComponent<
  'label',
  DefaultTheme,
  Record<string, unknown>
> = styled.label`
  ${({ showLabel, theme }: { showLabel?: boolean; theme: ITheme }) => css`
    display: flex;
    align-items: center;
    cursor: ${showLabel ? 'pointer' : 'default'};

    > ${StyledCheckbox} {
      margin-right: ${showLabel ? theme.spacing(1.5) : theme.spacing(0)};
    }

    > p {
      margin: 0;
    }
  `}
`;

const StyledInputFeedback = styled(InputFeedback)`
  margin-left: 3px; // Align with default checkbox margin
`;

const HintWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `}
`;
